import api from './api'

class StockService {
  // ===========================================
  // PRODUITS
  // ===========================================
  
  async getProducts(params = {}) {
    const response = await api.get('/stock/products', { params })
    return response.data
  }

  async getProduct(id) {
    const response = await api.get(`/stock/products/${id}`)
    return response.data
  }

  async createProduct(productData) {
    const response = await api.post('/stock/products', productData)
    return response.data
  }

  async updateProduct(id, productData) {
    const response = await api.put(`/stock/products/${id}`, productData)
    return response.data
  }

  async deleteProduct(id) {
    const response = await api.delete(`/stock/products/${id}`)
    return response.data
  }

  // ===========================================
  // MOUVEMENTS
  // ===========================================
  
  async getMovements(params = {}) {
    const response = await api.get('/movements', { params })
    return response.data
  }

  async getMovement(id) {
    const response = await api.get(`/movements/${id}`)
    return response.data
  }

  async createMovement(movementData) {
    const response = await api.post('/movements', movementData)
    return response.data
  }

  async validateMovement(id) {
    const response = await api.put(`/movements/${id}/validate`)
    return response.data
  }

  async cancelMovement(id) {
    const response = await api.put(`/movements/${id}/cancel`)
    return response.data
  }

  async getMovementStats(dateDebut, dateFin) {
    const response = await api.get('/movements/stats/period', {
      params: { dateDebut, dateFin }
    })
    return response.data
  }

  async getMovementsByTechnician(technicienId) {
    const response = await api.get(`/movements/technician/${technicienId}`)
    return response.data
  }

  // ===========================================
  // BONS DE SORTIE
  // ===========================================
  
  async getBonsSortie(params = {}) {
    const response = await api.get('/bons/sortie', { params })
    return response.data.data ? { data: response.data.data } : response.data
  }

  async getBonSortie(id) {
    const response = await api.get(`/bons/sortie/${id}`)
    return response.data
  }

  async createBonSortie(bonData) {
    const response = await api.post('/bons/sortie', bonData)
    return response.data
  }

  async signBonSortie(id, signature) {
    const response = await api.put(`/bons/sortie/${id}/sign`, { signature })
    return response.data
  }

  // ===========================================
  // BONS DE RETOUR
  // ===========================================
  
  async getBonsRetour(params = {}) {
    const response = await api.get('/bons/retour', { params })
    return response.data.data ? { data: response.data.data } : response.data
  }

  async getBonRetour(id) {
    const response = await api.get(`/bons/retour/${id}`)
    return response.data
  }

  async createBonRetour(bonData) {
    const response = await api.post('/bons/retour', bonData)
    return response.data
  }

  async verifyBonRetour(id, observations, articlesRefuses = []) {
    const response = await api.put(`/bons/retour/${id}/verify`, {
      observations,
      articlesRefuses
    })
    return response.data
  }

  // ===========================================
  // EMPLACEMENTS
  // ===========================================
  
  async getEmplacements(params = {}) {
    const response = await api.get('/emplacements', { params })
    return response.data
  }

  async getEmplacement(id) {
    const response = await api.get(`/emplacements/${id}`)
    return response.data
  }

  async createEmplacement(emplacementData) {
    const response = await api.post('/emplacements', emplacementData)
    return response.data
  }

  async updateEmplacement(id, emplacementData) {
    const response = await api.put(`/emplacements/${id}`, emplacementData)
    return response.data
  }

  async deleteEmplacement(id) {
    const response = await api.delete(`/emplacements/${id}`)
    return response.data
  }

  // ===========================================
  // TABLEAU DE BORD ET STATISTIQUES
  // ===========================================
  
  async getDashboard() {
    const response = await api.get('/stock/dashboard')
    return response.data
  }

  async getAlerts() {
    const response = await api.get('/stock/alerts')
    return response.data
  }

  async getBonsStats() {
    const response = await api.get('/bons/stats/dashboard')
    return response.data
  }

  // ===========================================
  // UTILITAIRES
  // ===========================================
  
  async searchProducts(query) {
    const response = await api.get('/stock/products', {
      params: { search: query, limit: 10 }
    })
    return response.data
  }

  async getProductsByCategory(categorie) {
    const response = await api.get('/stock/products', {
      params: { categorie }
    })
    return response.data
  }

  async getProductsByStatus(statut) {
    const response = await api.get('/stock/products', {
      params: { statut }
    })
    return response.data
  }

  async getProductsByLocation(emplacement) {
    const response = await api.get('/stock/products', {
      params: { emplacement }
    })
    return response.data
  }

  // ===========================================
  // RAPPORTS
  // ===========================================
  
  async generateInventoryReport(params = {}) {
    const response = await api.get('/stock/reports/inventory', { params })
    return response.data
  }

  async generateMovementsReport(dateDebut, dateFin, params = {}) {
    const response = await api.get('/stock/reports/movements', {
      params: { dateDebut, dateFin, ...params }
    })
    return response.data
  }

  async generateBonsReport(dateDebut, dateFin, type = 'all') {
    const response = await api.get('/stock/reports/bons', {
      params: { dateDebut, dateFin, type }
    })
    return response.data
  }

  // ===========================================
  // IMPORT/EXPORT
  // ===========================================
  
  async exportProducts(format = 'xlsx') {
    const response = await api.get('/stock/export/products', {
      params: { format },
      responseType: 'blob'
    })
    return response
  }

  async importProducts(file) {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/stock/import/products', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  async exportMovements(dateDebut, dateFin, format = 'xlsx') {
    const response = await api.get('/stock/export/movements', {
      params: { dateDebut, dateFin, format },
      responseType: 'blob'
    })
    return response
  }

  // ===========================================
  // CODES-BARRES ET QR CODES
  // ===========================================
  
  async generateBarcode(productId) {
    const response = await api.get(`/stock/products/${productId}/barcode`)
    return response.data
  }

  async generateQRCode(productId) {
    const response = await api.get(`/stock/products/${productId}/qrcode`)
    return response.data
  }

  async scanBarcode(code) {
    const response = await api.post('/stock/scan/barcode', { code })
    return response.data
  }

  async scanQRCode(code) {
    const response = await api.post('/stock/scan/qrcode', { code })
    return response.data
  }

  // ===========================================
  // VALIDATIONS ET RÈGLES MÉTIER
  // ===========================================
  
  async validateProductData(productData) {
    const response = await api.post('/stock/validate/product', productData)
    return response.data
  }

  async checkProductAvailability(productId, quantite) {
    const response = await api.post('/stock/check/availability', {
      productId,
      quantite
    })
    return response.data
  }

  async validateMovement(movementData) {
    const response = await api.post('/stock/validate/movement', movementData)
    return response.data
  }

  async checkBusinessRules(action, data) {
    const response = await api.post('/stock/check/rules', {
      action,
      data
    })
    return response.data
  }

  // ===========================================
  // NOTIFICATIONS ET ALERTES
  // ===========================================
  
  async getNotifications() {
    const response = await api.get('/stock/notifications')
    return response.data
  }

  async markNotificationAsRead(id) {
    const response = await api.put(`/stock/notifications/${id}/read`)
    return response.data
  }

  async getAlertSettings() {
    const response = await api.get('/stock/alerts/settings')
    return response.data
  }

  async updateAlertSettings(settings) {
    const response = await api.put('/stock/alerts/settings', settings)
    return response.data
  }

  // ===========================================
  // WORKFLOW TECHNICIEN-PRODUITS
  // ===========================================
  
  async getTechnicianProducts(technicienId) {
    const response = await api.get(`/stock/technicien/${technicienId}/produits`)
    return response.data
  }

  async getTechniciansReport() {
    const response = await api.get('/stock/rapport/techniciens')
    return response.data
  }

  async getRetoursReport() {
    const response = await api.get('/stock/rapport/retours')
    return response.data
  }

  async returnProductToStock(productId, emplacementRetour = 'magasin_central') {
    const response = await api.post(`/stock/produits/${productId}/retourner`, {
      emplacementRetour
    })
    return response.data
  }
}

export default new StockService()
